import * as Types from '../gql/_types.js';

import { GuidelineModule } from "./init";
import { mapArrayToStateObject } from "src/utils/state";
import { Action, AsyncAction, GraphQLSubscriptionResultsPayload } from 'src/state/_types';
import { subscriptions } from 'src/state/gql/_operations';
import { handleGraphqQLErrorResponse, handleGraphqQLSubscriptionErrorResponse } from 'src/state/gql/utils';
import { SetMetaInput } from 'src/state/Module';

const Module = GuidelineModule;

export const state = Module;

export const getItems: AsyncAction<Types.GetGuidelinesQueryVariables> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    state.guidelines.errors = [];
    state.guidelines.loading = true;
    try {
        let { limit, offset, orderBy } = state.guidelines.meta;
        let originalPayload = { limit, offset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetGuidelines({...originalPayload, ...finalPayload})
        state.guidelines.meta.total = meta.aggregate?.count || 0;
        state.guidelines.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.guidelines.errors = handleGraphqQLErrorResponse(e);
    }
    state.guidelines.loading = false;
}

export const resetState: Action = ({state}) => {
    state.guidelines.errors = []
    state.guidelines.loading = false
    state.guidelines.items = {}
    state.guidelines.currentItemId = null
}

export const resetMeta: Action = ({state}) => {
    state.guidelines.meta = {
        limit: Module.defaultLimit,
        sortOrder: Module.defaultSortOrder,
        sortKey: Module.defaultSortKey,
        page: 1,
        total: 0,
        offset: 0,
        orderBy: {}
    }
}

export const resetModule: AsyncAction = async ({actions}) => {
    actions.guidelines.resetMeta()
    actions.guidelines.resetState()
}

export const getMoreItems: AsyncAction = async ({state, effects}) => {
    let finalPayload = Module.mergeQueryPayload({});
    state.guidelines.errors = [];
    state.guidelines.loadingMore = true;
    try {
        let { limit, orderBy, page } = state.guidelines.meta;
        let newPage = ++page;
        let newOffset = ( limit * newPage ) - limit
        let originalPayload = { limit, offset: newOffset, order_by: orderBy };
        let { meta, items } = await effects.gql.queries.GetGuidelines({...originalPayload, ...finalPayload})
        state.guidelines.meta.total = meta.aggregate?.count || 0;
        state.guidelines.meta.page = newPage;
        state.guidelines.meta.offset = newOffset;
        state.guidelines.items = { ...state.guidelines.items, ...mapArrayToStateObject(items, 'id') };
    } catch (e: any) {
        state.guidelines.errors = handleGraphqQLErrorResponse(e);
    }
    state.guidelines.loadingMore = false;
}

export const getAllItems: AsyncAction<Types.GetGuidelinesQueryVariables> = async ({state, effects}, payload) => {
    state.guidelines.errors = [];
    state.guidelines.loading = true;
    try {
        let { meta, items } = await effects.gql.queries.GetGuidelines(payload)
        state.guidelines.meta.total = meta.aggregate?.count || 0;
        state.guidelines.items = mapArrayToStateObject(items, 'id');
    } catch (e: any) {
        state.guidelines.errors = handleGraphqQLErrorResponse(e);
    }
    state.guidelines.loading = false;
}

export const getItem: AsyncAction<Types.GetGuidelineQueryVariables,Types.GetGuidelineQuery['item']> = async ({state, effects}, payload) => {
    try {
        let { item } = await effects.gql.queries.GetGuideline(payload);
        return item;
    } catch (e: any) {
        state.guidelines.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const loadItem: AsyncAction<Types.GetGuidelineQueryVariables> = async ({state, actions}, payload) => {
    state.guidelines.loading = true;
    try {
        let item = await actions.guidelines.getItem(payload);
        if (item) {
            state.guidelines.currentItemId = item.id;
            state.guidelines.items[item.id] = item;
        }
    } catch (e: any) {
        state.guidelines.errors = handleGraphqQLErrorResponse(e);
    }
    state.guidelines.loading = false;
}

export const selectItem: Action<string> = async ({state}, payload) => {
    state.guidelines.currentItemId = payload
}

export const deselectItem: Action = async ({state}) => {
    state.guidelines.currentItemId = null
}

export const setMeta: Action<SetMetaInput<Types.GuidelineFragment>> = async ({state}, meta) => {
    state.guidelines.meta = {
        ...state.guidelines.meta,
        ...meta
    }
}


export const createItem: AsyncAction<Types.CreateGuidelineMutationVariables['object'],Types.CreateGuidelineMutation['item']> = async ({state, effects}, payload) => {
    let finalPayload = Module.mergeCreatePayload<Types.CreateGuidelineMutationVariables['object']>(payload);
    state.guidelines.errors = [];
    try {
        let { item } = await effects.gql.mutations.CreateGuideline({object:finalPayload});
        return item;
    } catch (e: any) {
        state.guidelines.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const updateItem: AsyncAction<Types.UpdateGuidelineMutationVariables,Types.UpdateGuidelineMutation['item']> = async ({state, effects}, payload) => {
    state.guidelines.errors = [];
    try {
        let { item } = await effects.gql.mutations.UpdateGuideline(payload);
        return item;
    } catch (e: any) {
        state.guidelines.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const deleteItem: AsyncAction<Types.DeleteGuidelineMutationVariables,Types.DeleteGuidelineMutation['item']> = async ({state, effects}, payload) => {
    state.guidelines.errors = [];
    try {
        let { item } = await effects.gql.mutations.DeleteGuideline(payload);
        return item;
    } catch (e: any) {
        state.guidelines.errors = handleGraphqQLErrorResponse(e);
        return null;
    }
}

export const syncGetItemsMeta: Action<Types.SyncGetGuidelinesMetaSubscriptionVariables> = async ({actions, effects}, {where}) => {
    effects.subscriptions.subscribe<Types.SyncGetGuidelinesMetaSubscription, Types.SyncGetGuidelinesMetaSubscriptionVariables>(
        'syncGetGuidelinesMeta', 
        subscriptions.SyncGetGuidelinesMeta, 
        { where }, 
        actions.guidelines.onItemsMeta
    );
}

export const unsubItemsMeta: Action = ({effects}) => {
    effects.subscriptions.unsubscribe('syncGetGuidelinesMeta');
}

export const onItemsMeta: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetGuidelinesMetaSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.guidelines.errors = [ ...state.guidelines.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data) {
        state.guidelines.meta.total = payload.data.meta.aggregate?.count || 0;
    }    
}

export const syncGetItems: Action<Types.SyncGetGuidelinesSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    let { limit, offset, orderBy } = state.guidelines.meta;
    let originalPayload = { limit, offset, order_by: orderBy };
    state.guidelines.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetGuidelinesSubscription, Types.SyncGetGuidelinesSubscriptionVariables>(
        'syncGetGuidelines', 
        subscriptions.SyncGetGuidelines, 
        {...originalPayload, ...payload}, 
        actions.guidelines.onItemsData,
        () => { state.guidelines.loading = false }
    );
}

export const unsubItemsData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetGuidelines');
    state.guidelines.items = {};
}

export const onItemsData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetGuidelinesSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.guidelines.errors = [ ...state.guidelines.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.items) {
        state.guidelines.items = mapArrayToStateObject(payload.data.items, 'id');
    }  
}

export const subscribeToItems: Action<Types.SyncGetGuidelinesSubscriptionVariables> = async ({actions}, payload) => {
    let finalPayload = Module.mergeQueryPayload(payload);
    actions.guidelines.syncGetItems(finalPayload);
    actions.guidelines.syncGetItemsMeta(finalPayload);
}

export const unsubscribeFromItems: Action = ({actions}) => {
    actions.guidelines.unsubItemsData();
    actions.guidelines.unsubItemsMeta();
}

export const syncGetItem: Action<Types.SyncGetGuidelineSubscriptionVariables> = async ({state, actions, effects}, payload) => {
    state.guidelines.loading = true;
    effects.subscriptions.subscribe<Types.SyncGetGuidelineSubscription, Types.SyncGetGuidelineSubscriptionVariables>(
        'syncGetGuideline', 
        subscriptions.SyncGetGuideline, 
        payload, 
        actions.guidelines.onItemData,
        () => { state.guidelines.loading = false }
    );
}

export const unsubItemData: Action = ({state,effects}) => {
    effects.subscriptions.unsubscribe('syncGetGuideline');
    state.guidelines.currentItemId = null;
}

export const onItemData: Action<GraphQLSubscriptionResultsPayload<Types.SyncGetGuidelineSubscription>> = async ({state}, payload) => {
    if (payload.error) {
        state.guidelines.errors = [ ...state.guidelines.errors, ...handleGraphqQLSubscriptionErrorResponse(payload.error) ];
    }
    if (payload.data&&payload.data.item) {
        state.guidelines.currentItemId = payload.data.item.id;
        state.guidelines.items[payload.data.item.id] = payload.data.item;
    }
}

export const subscribeToItem: Action<Types.SyncGetGuidelineSubscriptionVariables> = async ({actions}, payload) => {
    actions.guidelines.syncGetItem(payload);
}

export const unsubscribeFromItem: Action = ({actions}) => {
    actions.guidelines.unsubItemData();
}

export const actions = {
    getItems,
    getAllItems,
    resetState,
    resetMeta,
    getMoreItems,
    resetModule,
    getItem,
    loadItem,
    selectItem,
    deselectItem,
    setMeta,
    createItem,
    updateItem,
    deleteItem,
    syncGetItemsMeta,
    unsubItemsMeta,
    onItemsMeta,
    syncGetItems,
    unsubItemsData,
    onItemsData,
    subscribeToItems,
    unsubscribeFromItems,
    syncGetItem,
    unsubItemData,
    onItemData,
    subscribeToItem,
    unsubscribeFromItem
};